// ABOUTME: Who writes which calendar records — one registry, asked by scope, so no caller has to
// ABOUTME: infer ownership from mounted providers, document lists or the shape of a cache key.
import type { CalendarRange } from "@/model/calendar/cache/calendarRange"
import type { RefreshOutcome } from "@/model/calendar/cache/refreshOutcome"
import { NOTHING_TO_DO, combineRefreshOutcomes } from "@/model/calendar/cache/refreshOutcome"

/**
 * A set of calendar records: one athlete's inclusive day range. The same shape
 * a realtime event carries, because it is the same question — which records
 * does this concern.
 *
 * `userId: null` means the signed-in user's OWN data. Several queries express
 * that by omitting `userID` altogether (the health summary does), and reading
 * that as "no owner" sent every one of those queries back to the network
 * beside the window that was already writing them.
 */
export type CalendarScope = Omit<CalendarRange, "userId"> & {
  userId: string | null
  /**
   * The exact days one cache entry holds, when the scope describes a QUERY
   * rather than a change. An owner writes whole weeks under a key built from
   * their seven dates, so a one-day `calendarItems` query overlaps its range
   * without sharing a single record with it — and answering "owned" there left
   * that query stale with nobody to refresh it.
   */
  dates?: readonly string[]
}

/** Which family of records a refresh is about; owners hold them separately. */
export type RecordFamily = "calendar" | "health"

/**
 * Something that writes calendar records and is therefore the only thing that
 * may refresh them: the window loader for the weeks on screen, the chunk
 * fetchers for the coach's list.
 *
 * The rule this registry exists to keep: a record with an owner is refreshed
 * through it, never with a second request of its own. Two writers of the same
 * record can answer out of order, and the older answer landing second undoes
 * the change that asked for the newer one.
 */
export interface RecordOwner {
  /** True when this owner writes the records of that scope. */
  owns: (scope: CalendarScope) => boolean
  /** Refreshes what it owns of these scopes and families; `null` means everything it owns. */
  refresh: (scopes: readonly CalendarScope[] | null, families: readonly RecordFamily[]) => Promise<RefreshOutcome>
}

/**
 * Every mounted owner, newest last. A stack rather than a slot: two can briefly
 * coexist while a tree is rebuilt, and the second one's unmount must not
 * unregister the survivor.
 */
const owners: RecordOwner[] = []

export function registerRecordOwner(owner: RecordOwner): () => void {
  owners.push(owner)
  return () => {
    const at = owners.lastIndexOf(owner)
    if (at !== -1) owners.splice(at, 1)
  }
}

/** Whether anything at all owns calendar records right now. */
export function hasRecordOwners(): boolean {
  return owners.length > 0
}

/**
 * Whether some owner writes these records — i.e. whether a caller must go
 * through it instead of refetching them itself.
 */
export function isOwned(scope: CalendarScope): boolean {
  return owners.some((owner) => owner.owns(scope))
}

/**
 * Refreshes these scopes through every owner that holds any of them. All of
 * them, not the first: the window and the chunk fetchers own different records,
 * and stopping at one would leave the other's rows on pre-mutation data.
 */
export function refreshOwners(
  scopes: readonly CalendarScope[] | null,
  families: readonly RecordFamily[],
): Promise<RefreshOutcome> {
  if (owners.length === 0 || families.length === 0) return Promise.resolve(NOTHING_TO_DO)
  return Promise.all(owners.map((owner) => owner.refresh(scopes, families))).then(combineRefreshOutcomes)
}

/** Test seam — the registry outlives a test file otherwise. */
export function resetRecordOwnersForTests(): void {
  owners.length = 0
}
